import { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import axios from "axios";
import LandingPage from "@/pages/LandingPage";
import Dashboard from "@/pages/Dashboard";
import CampsiteDetail from "@/pages/CampsiteDetail";
import Login from "@/pages/Login";
import Signup from "@/pages/Signup";
import PaymentSuccess from "@/pages/PaymentSuccess";
import AdminNotify from "@/pages/AdminNotify";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";

// Get backend URL and ensure it ends with /api (but not /api/api)
let BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
// Remove trailing slash if present
BACKEND_URL = BACKEND_URL.replace(/\/+$/, '');
// Remove /api suffix if present (we'll add it consistently)
BACKEND_URL = BACKEND_URL.replace(/\/api$/, '');
// Now add /api consistently
const API = `${BACKEND_URL}/api`;

export const axiosInstance = axios.create({
  baseURL: API,
  withCredentials: true,
});

console.log('🔍 DEBUG: Backend URL (raw):', process.env.REACT_APP_BACKEND_URL);
console.log('🔍 DEBUG: API baseURL (normalized):', API);

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    // Check if there's a session_id in URL hash first (OAuth callback)
    const hash = window.location.hash;
    const sessionIdMatch = hash.match(/session_id=([^&]+)/);
    
    if (sessionIdMatch) {
      const sessionId = sessionIdMatch[1];
      try {
        const response = await axiosInstance.post('/auth/session', { session_id: sessionId });
        if (response.data.success) {
          setUser(response.data.user);
          toast.success('Successfully logged in with Google!');
          // Clean the URL - remove the hash
          window.history.replaceState({}, document.title, window.location.pathname);
        }
      } catch (error) {
        console.error('Session processing error:', error);
        toast.error('Failed to complete Google login');
      } finally {
        setLoading(false);
      }
      return;
    }

    // No session_id, check if user is already logged in
    try {
      const response = await axiosInstance.get("/auth/me");
      setUser(response.data);
    } catch (error) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-orange-50 to-beige-100">
        <div className="text-xl font-medium">Loading...</div>
      </div>
    );
  }

  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<LandingPage user={user} setUser={setUser} />} />
          <Route 
            path="/login" 
            element={!user ? <Login setUser={setUser} /> : <Navigate to="/dashboard" />} 
          />
          <Route 
            path="/signup" 
            element={!user ? <Signup setUser={setUser} /> : <Navigate to="/dashboard" />} 
          />
          <Route 
            path="/dashboard" 
            element={user ? <Dashboard user={user} setUser={setUser} /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/payment-success" 
            element={user ? <PaymentSuccess setUser={setUser} /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/campsite/:id" 
            element={<CampsiteDetail user={user} />} 
          />
          <Route 
            path="/admin/notify" 
            element={<AdminNotify />} 
          />
        </Routes>
      </BrowserRouter>
      <Toaster />
    </div>
  );
}

export default App;
